import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CatalogFormSchemaEditor } from '../../components/metadata/CatalogFormSchemaEditor';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  ServiceCatalogItem,
  ServiceCatalogItemUpsertPayload,
  createServiceCatalogItem,
  deleteServiceCatalogItem,
  listServiceCatalogItems,
  updateServiceCatalogItem,
} from '../../services/serviceCatalogApi';
import {
  ServiceTicketSlaOption,
  ServiceTicketTeam,
  listTicketSlaOptions,
  listTicketTeams,
} from '../../services/customerSuccessTicketsApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type ServiceCatalogFormState = Omit<ServiceCatalogItemUpsertPayload, 'sortOrder'>;

const emptyForm: ServiceCatalogFormState = {
  name: '',
  description: '',
  formSchemaJson: '',
  responseTemplate: '',
  defaultPriority: 'Medium',
  autoFulfill: false,
  isActive: true,
};

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

function buildServiceCatalogPayload(
  item: ServiceCatalogItem,
  sortOrder: number,
): ServiceCatalogItemUpsertPayload {
  return {
    name: item.name,
    description: item.description,
    ...(item.formSchemaJson != null ? { formSchemaJson: item.formSchemaJson } : {}),
    ...(item.responseTemplate != null ? { responseTemplate: item.responseTemplate } : {}),
    ...(item.defaultSlaId != null ? { defaultSlaId: item.defaultSlaId } : {}),
    ...(item.defaultAssignedTeamId != null ? { defaultAssignedTeamId: item.defaultAssignedTeamId } : {}),
    defaultPriority: item.defaultPriority,
    autoFulfill: item.autoFulfill,
    isActive: item.isActive,
    sortOrder,
  };
}

export function ServiceCatalogPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.service-catalog');
  const [items, setItems] = useState<ServiceCatalogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pageSize] = useState(500);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ServiceCatalogFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState<ServiceTicketTeam[]>([]);
  const [slaOptions, setSlaOptions] = useState<ServiceTicketSlaOption[]>([]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const [result, teamList, slas] = await Promise.all([
        listServiceCatalogItems({ page: targetPage, pageSize, search: search || undefined }),
        listTicketTeams(),
        listTicketSlaOptions(),
      ]);
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
      setTeams(teamList);
      setSlaOptions(slas);
    } catch {
      toast.error('Falha ao carregar catálogo de serviços.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateServiceCatalogItem(item.id, buildServiceCatalogPayload(item, sortOrder));
    },
    onReordered: () => loadData(page),
  });

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: ServiceCatalogItem): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      formSchemaJson: item.formSchemaJson ?? '',
      responseTemplate: item.responseTemplate ?? '',
      defaultSlaId: item.defaultSlaId ?? undefined,
      defaultAssignedTeamId: item.defaultAssignedTeamId ?? undefined,
      defaultPriority: item.defaultPriority,
      autoFulfill: item.autoFulfill,
      isActive: item.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: ServiceCatalogItemUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(items),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateServiceCatalogItem(editingId, payload);
        toast.success('Item de catálogo atualizado.');
        resetForm();
      } else {
        await createServiceCatalogItem(payload);
        toast.success('Item de catálogo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar item de catálogo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: ServiceCatalogItem): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir item',
        message: `Excluir "${item.name}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteServiceCatalogItem(item.id);
      toast.success('Item excluído.');
      if (editingId === item.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir item.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Catálogo de serviços"
        description="Solicitações padronizadas com formulários, SLA padrão e atendimento automático."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar item' : 'Novo item de catálogo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'item selecionado'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar um novo item de catálogo"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="space-y-4">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome <RequiredMark />
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Prioridade padrão
                <select
                  className="erp-input"
                  value={form.defaultPriority ?? 'Medium'}
                  onChange={(e) => setForm({ ...form, defaultPriority: e.target.value })}
                >
                  <option value="Low">Baixa</option>
                  <option value="Medium">Média</option>
                  <option value="High">Alta</option>
                  <option value="Critical">Crítica</option>
                </select>
              </label>
              <label className="erp-label">
                SLA padrão
                <select
                  className="erp-input"
                  value={form.defaultSlaId ?? ''}
                  onChange={(e) => setForm({ ...form, defaultSlaId: e.target.value || undefined })}
                >
                  <option value="">Nenhum</option>
                  {slaOptions.map((sla) => (
                    <option key={sla.id} value={sla.id}>
                      {sla.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Equipe padrão
                <select
                  className="erp-input"
                  value={form.defaultAssignedTeamId ?? ''}
                  onChange={(e) => setForm({ ...form, defaultAssignedTeamId: e.target.value || undefined })}
                >
                  <option value="">Nenhuma</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="erp-label">
              Descrição
              <textarea
                className="erp-input min-h-[80px]"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <div className="mt-3">
              <CatalogFormSchemaEditor
                value={form.formSchemaJson}
                onChange={(formSchemaJson) => setForm({ ...form, formSchemaJson })}
              />
            </div>
            <label className="erp-label">
              Template de resposta automática
              <textarea
                className="erp-input min-h-[80px]"
                value={form.responseTemplate ?? ''}
                onChange={(e) => setForm({ ...form, responseTemplate: e.target.value })}
              />
            </label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.autoFulfill ?? false}
                  onChange={(e) => setForm({ ...form, autoFulfill: e.target.checked })}
                />
                Atendimento automático
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Ativo
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </div>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex gap-2">
        <input
          className="erp-input max-w-md"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando catálogo..." />
      ) : (
        <>
          <div className="erp-card overflow-x-auto">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  {canWrite ? <th className="w-10" /> : null}
                  <th>Nome</th>
                  <th>Prioridade</th>
                  <th>SLA</th>
                  <th>Auto</th>
                  <th>Status</th>
                  {canWrite ? <th className="w-24" /> : null}
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 7 : 5} className="text-center text-secondary-500">
                      Nenhum item de catálogo cadastrado.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item, index) => (
                    <tr key={item.id}>
                      {canWrite ? (
                        <td>
                          <SortOrderControls
                            canMoveUp={index > 0}
                            canMoveDown={index < sortedItems.length - 1}
                            disabled={moving}
                            onMoveUp={() => void move(index, 'up')}
                            onMoveDown={() => void move(index, 'down')}
                          />
                        </td>
                      ) : null}
                      <td>
                        <div className="font-medium">{item.name}</div>
                        {item.description ? (
                          <div className="text-xs text-secondary-500 line-clamp-1">{item.description}</div>
                        ) : null}
                      </td>
                      <td>{PRIORITY_LABELS[item.defaultPriority] ?? item.defaultPriority}</td>
                      <td>{item.defaultSlaName ?? '—'}</td>
                      <td>{item.autoFulfill ? 'Sim' : 'Não'}</td>
                      <td>
                        <ActiveStatusBadge active={item.isActive} />
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} />
                            <DeleteIconButton onClick={() => void handleDelete(item)} />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > pageSize ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
